/**
 * Create-environment dialog. The user picks the kind (production/staging/etc.),
 * names it, and optionally writes a description. Kind is permanent after creation
 * — changing prod→dev would silently weaken safety checks downstream, so the
 * patch endpoint doesn't accept it. The user can delete and recreate if they
 * really need to change it.
 */
import { useState, type FormEvent } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog.js';
import { Button } from '@/components/ui/Button.js';
import { Input } from '@/components/ui/Input.js';
import { Label } from '@/components/ui/Label.js';
import { api, ApiError, type EnvironmentKind } from '@/api/client.js';
import { cn } from '@/lib/utils.js';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  applicationId: string;
  applicationName: string;
  /** Slugs already taken in this app — pre-validates on the client. */
  takenSlugs: string[];
  onCreated: () => void | Promise<void>;
}

const KIND_OPTIONS: Array<{
  value: EnvironmentKind;
  label: string;
  hint: string;
  dotClass: string;
  defaultName: string;
  defaultSlug: string;
}> = [
  {
    value: 'production',
    label: 'Production',
    hint: 'Live, customer-facing. Future safety checks gate destructive actions here.',
    dotClass: 'bg-status-down shadow-[0_0_0_3px_hsl(var(--status-down)/0.2)]',
    defaultName: 'Production',
    defaultSlug: 'production',
  },
  {
    value: 'staging',
    label: 'Staging',
    hint: 'Pre-production. Often refreshed from prod via a scrub pipeline.',
    dotClass: 'bg-status-degraded shadow-[0_0_0_3px_hsl(var(--status-degraded)/0.2)]',
    defaultName: 'Staging',
    defaultSlug: 'staging',
  },
  {
    value: 'development',
    label: 'Development',
    hint: 'Day-to-day dev. Often seeded from staging.',
    dotClass: 'bg-status-up shadow-[0_0_0_3px_hsl(var(--status-up)/0.2)]',
    defaultName: 'Development',
    defaultSlug: 'dev',
  },
  {
    value: 'preview',
    label: 'Preview',
    hint: 'Per-branch / per-PR ephemeral instances.',
    dotClass: 'bg-primary shadow-[0_0_0_3px_hsl(var(--primary)/0.2)]',
    defaultName: 'Preview',
    defaultSlug: 'preview',
  },
  {
    value: 'other',
    label: 'Other',
    hint: 'Anything else (demo, QA, sandbox, …).',
    dotClass: 'bg-status-unknown shadow-[0_0_0_3px_hsl(var(--status-unknown)/0.2)]',
    defaultName: '',
    defaultSlug: '',
  },
];

export function CreateEnvironmentDialog({
  open,
  onOpenChange,
  applicationId,
  applicationName,
  takenSlugs,
  onCreated,
}: Props): JSX.Element {
  // Default selection: prod if it doesn't exist yet, otherwise staging, otherwise dev.
  const initialKind: EnvironmentKind =
    !takenSlugs.includes('production')
      ? 'production'
      : !takenSlugs.includes('staging')
        ? 'staging'
        : !takenSlugs.includes('dev')
          ? 'development'
          : 'other';
  const initialOpt = KIND_OPTIONS.find((o) => o.value === initialKind) ?? KIND_OPTIONS[0]!;

  const [kind, setKind] = useState<EnvironmentKind>(initialKind);
  const [name, setName] = useState(initialOpt.defaultName);
  const [slug, setSlug] = useState(initialOpt.defaultSlug);
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function pickKind(k: EnvironmentKind) {
    const opt = KIND_OPTIONS.find((o) => o.value === k)!;
    setKind(k);
    // Only auto-fill the name/slug if the user hasn't customised them yet — i.e.
    // they still equal the previous option's default.
    setName((cur) => {
      const prev = KIND_OPTIONS.find((o) => o.defaultName === cur);
      return prev ? opt.defaultName : cur;
    });
    setSlug((cur) => {
      const prev = KIND_OPTIONS.find((o) => o.defaultSlug === cur);
      return prev ? opt.defaultSlug : cur;
    });
  }

  function reset() {
    setKind(initialKind);
    setName(initialOpt.defaultName);
    setSlug(initialOpt.defaultSlug);
    setDescription('');
    setError(null);
    setSubmitting(false);
  }

  const slugTaken = slug.trim().length > 0 && takenSlugs.includes(slug.trim().toLowerCase());

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (slugTaken) {
      setError(`An environment with slug "${slug}" already exists in this application.`);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await api.environments.create(applicationId, {
        name,
        kind,
        ...(slug ? { slug } : {}),
        ...(description ? { description } : {}),
      });
      toast.success(`Created ${name}`);
      await onCreated();
      onOpenChange(false);
      reset();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not create environment.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New environment</DialogTitle>
          <DialogDescription>
            An environment is a tenancy of <strong className="text-foreground">{applicationName}</strong> —
            production, staging, dev, etc. Kind is set now and can't be changed later (delete and
            recreate if you need to).
          </DialogDescription>
        </DialogHeader>

        <form className="space-y-4" onSubmit={submit}>
          <div className="space-y-1.5">
            <Label>Kind</Label>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {KIND_OPTIONS.map((opt) => (
                <button
                  type="button"
                  key={opt.value}
                  onClick={() => pickKind(opt.value)}
                  className={cn(
                    'flex items-start gap-2.5 rounded-md border px-3 py-2 text-left transition-colors',
                    kind === opt.value
                      ? 'border-primary bg-primary/10'
                      : 'border-border bg-background hover:bg-accent/40',
                  )}
                >
                  <span className={cn('mt-1 h-2.5 w-2.5 shrink-0 rounded-full', opt.dotClass)} />
                  <span className="flex flex-col gap-0.5">
                    <span className="text-sm font-medium">{opt.label}</span>
                    <span className="text-[11px] text-muted-foreground">{opt.hint}</span>
                  </span>
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              required
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Production"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="slug">Slug</Label>
            <Input
              id="slug"
              value={slug}
              onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
              placeholder="prod"
            />
            <p className="text-xs text-muted-foreground">Used in URLs and CLI. Lowercase, dashes only.</p>
            {slugTaken && (
              <p className="text-xs text-destructive">Already taken in this application.</p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="desc">Description</Label>
            <Input
              id="desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional"
            />
          </div>

          {error && (
            <p className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </p>
          )}

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting || !name || slugTaken}>
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create environment'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
